import { useEffect, useState } from 'react'
import { MapPin, Table } from 'lucide-react'
import { CrossIcon } from '@/assets/svgIcons'
import { Button } from '@/designs/new/components/ui/button/button'
import { createSavedSites } from '@/services/apis/api'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { toast } from 'sonner'

interface SaveViewModalProps {
  isOpen: boolean
  viewName: string
  viewDescription: string
  onViewNameChange: (val: string) => void
  onViewDescriptionChange: (val: string) => void
  onSave: () => void
  onClose: () => void
  defaultType?: 'site' | 'table' | 'workspace'
  onSaveSuccess?: () => void
}

const SaveViewModal = ({
  isOpen,
  viewName,
  viewDescription,
  onViewNameChange,
  onViewDescriptionChange,
  onSave,
  onClose,
  defaultType = 'table',
  onSaveSuccess,
}: SaveViewModalProps) => {
  const [selectedType, setSelectedType] = useState<
    'site' | 'table' | 'workspace'
  >(defaultType)
  const [isSaving, setIsSaving] = useState(false)

  const payloadDef = useAppSelector(state => state.menu.payloadDef)
  const siteDetails = useAppSelector(state => state.menu.siteDetails) || []

  useEffect(() => {
    if (isOpen) {
      setSelectedType(defaultType)
    }
  }, [isOpen, defaultType])

  if (!isOpen) return null

  const handleSave = async () => {
    const trimmedName = viewName.trim()
    if (!trimmedName) {
      toast.error('View name is required')
      return
    }
    if (trimmedName.length < 3 || trimmedName.length > 50) {
      toast.error('View name must be between 3 and 50 characters')
      return
    }
    const trimmedDesc = viewDescription.trim()
    if (!trimmedDesc) {
      toast.error('Description is required')
      return
    }
    if (trimmedDesc.length < 3 || trimmedDesc.length > 250) {
      toast.error('Description must be between 3 and 250 characters')
      return
    }

    setIsSaving(true)
    try {
      let siteSaveSuccess = true

      if (selectedType === 'site' || selectedType === 'workspace') {
        const payload: Record<string, unknown> = {
          name: viewName.trim(),
          description: viewDescription.trim(),
          selectedSites: siteDetails.map(site => ({
            Id: site.Id || site.value || '',
            label: site.label || site.Name || '',
            Rev_io_Account_ID__c: site.Rev_io_Account_ID__c,
            value: site.value || site.Id || '',
          })),
          type: payloadDef.billingSubGroup ? 'billSubgroup' : 'parentCustomer',
        }
        if (payloadDef.billingSubGroup) {
          payload.label = payloadDef.billingSubGroup
          payload.value = payloadDef.billingSubGroup
        } else if (payloadDef.customerId && payloadDef.customerId.length > 0) {
          payload.value = Array.isArray(payloadDef.customerId)
            ? payloadDef.customerId[0]
            : payloadDef.customerId
          payload.label = payloadDef.customerName
        }

        try {
          await createSavedSites(payload)
          toast.success('Site view saved successfully')
          onSaveSuccess?.()
        } catch (err) {
          siteSaveSuccess = false
          console.error(err)
          toast.error('Failed to save site view')
        }
      }

      if (selectedType === 'table' || selectedType === 'workspace') {
        if (siteSaveSuccess) {
          // Trigger the parent's table save action
          await onSave()
        }
      } else {
        // If it was just site view saving, we close the modal here
        if (siteSaveSuccess) {
          onClose()
        }
      }
    } catch (error) {
      console.error('Error in handleSave:', error)
      toast.error('Failed to save view')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      className='fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 rounded-radius-sm'
      onClick={onClose}
    >
      <div
        className='bg-primary p-6 rounded-xl w-[500px] flex flex-col gap-5 shadow-xl border border-border transition-all duration-200'
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex flex-col gap-1'>
          <div className='flex justify-between items-center text-[20px] font-bold text-primary-foreground'>
            <p>Save Current View</p>
            <div
              onClick={onClose}
              className='cursor-pointer text-secondary-foreground hover:text-primary-foreground transition-colors'
            >
              <CrossIcon />
            </div>
          </div>
          <p className='text-sm text-secondary-foreground font-normal'>
            What do you want to save?
          </p>
        </div>

        {/* Options */}
        <div className='grid grid-cols-2 gap-3'>
          {/* Site View */}
          <div
            className={`flex-1 border rounded-xl p-3 flex flex-col items-center justify-center gap-2 cursor-pointer text-center transition-all duration-150 select-none ${
              selectedType === 'site'
                ? 'border-accent bg-accent/5 text-accent font-semibold shadow-sm'
                : 'border-border bg-transparent text-primary-foreground hover:bg-accent-hover/5 font-medium'
            }`}
            onClick={() => setSelectedType('site')}
          >
            <MapPin
              className={`w-6 h-6 ${selectedType === 'site' ? 'text-accent' : 'text-secondary-foreground'}`}
            />
            <div className='flex flex-col gap-0.5'>
              <p className='text-[14px] leading-tight'>Site View</p>
              <p className='text-[11px] text-secondary-foreground font-normal leading-tight'>
                Customer + Sites
              </p>
            </div>
          </div>

          {/* Table View */}
          <div
            className={`flex-1 border rounded-xl p-3 flex flex-col items-center justify-center gap-2 cursor-not-allowed opacity-50 text-center transition-all duration-150 select-none ${
              selectedType === 'table'
                ? 'border-accent bg-accent/5 text-accent font-semibold shadow-sm'
                : 'border-border bg-transparent text-primary-foreground font-medium'
            }`}
          >
            <Table
              className={`w-6 h-6 ${selectedType === 'table' ? 'text-accent' : 'text-secondary-foreground'}`}
            />
            <div className='flex flex-col gap-0.5'>
              <p className='text-[14px] leading-tight'>Table View</p>
              <p className='text-[11px] text-secondary-foreground font-normal leading-tight'>
                Columns + Filters
              </p>
            </div>
          </div>

          {/* Workspace */}
          {/* <div
            className={`flex-1 border rounded-xl p-3 flex flex-col items-center justify-center gap-2 cursor-pointer text-center transition-all duration-150 select-none ${
              selectedType === 'workspace'
                ? 'border-accent bg-accent/5 text-accent font-semibold shadow-sm'
                : 'border-border bg-transparent text-primary-foreground hover:bg-accent-hover/5 font-medium'
            }`}
            onClick={() => setSelectedType('workspace')}
          >
            <LayoutGrid
              className={`w-6 h-6 ${selectedType === 'workspace' ? 'text-accent' : 'text-secondary-foreground'}`}
            />
            <div className='flex flex-col gap-0.5'>
              <p className='text-[14px] leading-tight'>Workspace</p>
              <p className='text-[11px] text-secondary-foreground font-normal leading-tight'>
                Both sites + table
              </p>
            </div>
          </div> */}
        </div>

        {/* Form Fields */}
        <div className='flex flex-col gap-4'>
          <div>
            <label className='text-[15px] text-primary-foreground font-semibold mb-1 block'>
              View Name <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className={`w-full px-3 py-2 border bg-primary text-primary-foreground rounded-lg focus:outline-none focus:ring-2 text-sm ${
                viewName.trim() &&
                (viewName.trim().length < 3 || viewName.trim().length > 50)
                  ? 'border-danger focus:ring-danger'
                  : 'border-border focus:ring-blue-500'
              }`}
              placeholder='Enter view name'
              value={viewName}
              onChange={e => onViewNameChange(e.target.value)}
            />
            {viewName.trim() &&
              (viewName.trim().length < 3 || viewName.trim().length > 50) && (
                <p className='text-xs text-danger mt-1'>
                  View name must be between 3 and 50 characters
                </p>
              )}
          </div>
          <div>
            <label className='text-[15px] text-primary-foreground font-semibold mb-1 block'>
              Description <span className='text-danger'>*</span>
            </label>
            <textarea
              className={`w-full px-3 py-2 border bg-primary text-primary-foreground rounded-lg focus:outline-none focus:ring-2 min-h-[100px] text-sm ${
                viewDescription.trim() &&
                (viewDescription.trim().length < 3 ||
                  viewDescription.trim().length > 250)
                  ? 'border-danger focus:ring-danger'
                  : 'border-border focus:ring-blue-500'
              }`}
              placeholder='Enter description'
              value={viewDescription}
              onChange={e => onViewDescriptionChange(e.target.value)}
            />
            {viewDescription.trim() &&
              (viewDescription.trim().length < 3 ||
                viewDescription.trim().length > 250) && (
                <p className='text-xs text-danger mt-1'>
                  Description must be between 3 and 250 characters
                </p>
              )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className='flex justify-end items-center gap-3 mt-1'>
          <Button
            variant='outline'
            onClick={onClose}
            className='bg-transparent text-[#0f4086] border-border hover:bg-accent-hover/10 rounded-lg px-6 h-10 font-semibold text-sm'
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className='bg-[#0f4086] hover:bg-[#0f4086]/90 text-white rounded-lg px-6 h-10 font-semibold text-sm'
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SaveViewModal
